import React, { FC } from 'react';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';

import Text from '../Text';

import UtilFunctions from '~utils/functions';
import * as DatepickerStyles from './_labelledDatepicker.module.scss';

type LabelledDatepickerProps = {
    label: string,
    setSelectedDate: (date: string) => void
    selectedDate?: string,
    minDate?: string;
    maxDate?: string;
    isRow?: boolean;
    isFilter?: boolean;
    id?: string;
    name?: string;
    onBlur?: (e: React.FocusEvent<HTMLElement, Element>) => void;
    error?: string;
    disabled?: boolean;
    startDate?: string;
    endDate?: string;
    selectsStart?: boolean;
    selectsEnd?: boolean;
    selectTime?: boolean;
    filterDate?: (date: Date) => boolean;
}

const LabelledDatepicker: FC<LabelledDatepickerProps> = ({
    label,
    selectedDate,
    setSelectedDate,
    minDate,
    maxDate,
    isRow,
    isFilter,
    id,
    name,
    onBlur,
    error,
    disabled,
    startDate,
    endDate,
    selectsStart,
    selectsEnd,
    selectTime,
    filterDate,
}) => {
    const wrapperClassName = UtilFunctions.Styles.concatClassNames([
        DatepickerStyles['wrapper-container'],
        (isFilter ? DatepickerStyles['is-filter'] : ''),
        (disabled ? DatepickerStyles['is-disabled'] : ''),
    ]);

    const handleSelectedDate = (date: Date | null) => {
        if (!date?.getTime()) return;

        const massagedDate = selectsEnd ? dayjs(date).endOf('day').toISOString() : date?.toISOString();

        setSelectedDate(massagedDate || '');
    };

    return (
        <div className={`${DatepickerStyles.container}${isRow ? ` ${DatepickerStyles['is-row']}` : ''}`}>
            <Text
                isBold
                label={label}
                className={DatepickerStyles.label}
            />

            <DatePicker
                id={id}
                name={name}
                wrapperClassName={wrapperClassName}
                calendarIconClassName={DatepickerStyles['calendar-icon']}
                calendarClassName={DatepickerStyles['calendar-container']}
                monthClassName={() => DatepickerStyles['month-container']}
                weekDayClassName={() => DatepickerStyles['week-container']}
                dayClassName={() => DatepickerStyles['day-container']}
                showIcon
                showYearDropdown
                showMonthDropdown
                dropdownMode='select'
                dateFormat={selectTime ? 'dd/MM/yyyy h:mm aa' : 'dd/MM/yyyy'}
                placeholderText={selectTime ? 'DD/MM/YYYY H:MM am/pm' : 'DD/MM/YYYY'}
                selected={selectedDate ? dayjs(selectedDate).toDate() : null}
                onChange={handleSelectedDate}
                minDate={minDate ? new Date(minDate) : undefined}
                maxDate={maxDate ? new Date(maxDate) : undefined}
                onBlur={onBlur}
                autoComplete='off'
                disabled={disabled}
                toggleCalendarOnIconClick
                startDate={startDate ? new Date(startDate) : undefined}
                endDate={endDate ? new Date(endDate) : undefined}
                selectsStart={selectsStart}
                selectsEnd={selectsEnd}
                timeInputLabel='Time:'
                showTimeInput={selectTime}
                filterDate={filterDate}
            />

            {error
                && (
                    <Text
                        label={error}
                        isError
                        className={DatepickerStyles.error}
                    />
                )}
        </div>
    );
};

export default LabelledDatepicker;
